import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { WorkflowToken } from '../../../../core/enums/workflow-token.enum';
import { User } from '../../../../core/models/user.model';
import { AuthService } from '../../../../core/services/auth.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { SessionService } from '../../../../core/services/session.service';
import { WindowRefService } from '../../../../core/services/window.service';
import { Country } from 'src/app/modules/core/models/country.model';
import { MetaInfoService } from 'src/app/modules/core/services/meta-info.service';
import { CompanyType } from 'src/app/modules/core/enums/company-types.enum';

@Component({
	selector: 'cfe-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
	public isCollapsed = true;

	public loggedIn = false;

	public name: string;

	public messageKey = 'header.text.dsCoreAdvertisement';
	public shutdownDate = new Date();
	public currentLanguage = this.translateService.currentLang;
	public dateOptions = { year: 'numeric', month: 'long', day: 'numeric' } as const;

	// Links
	aboutSironaLink: string;
	serviceLink: string;
	contactLink: string;

	// Current user
	public user: User;

	// Is allowed to edit profile or change password
	public editable = false;

	// Show an additional banner in the header next to the logo
	public showHeaderBanner: boolean;

	// Can hide the button in the header banner
	public showHeaderBannerButton: boolean;

	// Show an additional important message in the banner
	public showImportantMessage: boolean;

	// User subscription
	protected subscriptions: Subscription[] = [];

	private lastReceivedServerTime: Date;
	private lastServerTimeSynchronization: DOMHighResTimeStamp;

	get noticeHeader() {
		return this.configService.config.noticeHeader;
	}

	get noticeInfo() {
		return this.configService.config.noticeInfo;
	}

	constructor(
		private configService: ConfigService,
		private translateService: TranslateService,
		private sessionService: SessionService,
		private authService: AuthService,
		private windowRef: WindowRefService,
		private loader: LoaderService,
		private metaInfoService: MetaInfoService
	) {}

	/**
	 * On init
	 */
	ngOnInit() {
		this.showHeaderBanner = this.configService.config.showHeaderBanner;
		this.showHeaderBannerButton = this.configService.config.showHeaderBannerButton;
		const now = new Date();
		this.showImportantMessage =
			new Date(this.configService.config.importantMessageStartDate) <= now &&
			new Date(this.configService.config.importantMessageEndDate) >= now;

		// Set logged in status
		this.authService.loggedIn(true, false).subscribe((loggedIn) => {
			this.loggedIn = loggedIn;
			this.subscriptions.push(
				this.sessionService.currentUserObservable.subscribe((user) => {
					if (user) {
						this.setBannerForCountry(user.company.billingAddress.country, user.company.extendedShutdownDate, user.company.type);
						this.user = user;
						this.name = this.user.firstName + ' ' + this.user.lastName;
						this.editable = this.user.isAuthorized({
							workflows: [WorkflowToken.ACCOUNT_USER_SELF_ADMINISTRATION, WorkflowToken.ACCOUNT_USER_RESET_PASSWORD]
						});
						this.authService.loggedIn(true, false).subscribe((stillLoggedIn) => {
							this.loggedIn = stillLoggedIn;
						});
					} else {
						this.loggedIn = false;
						this.messageKey = 'header.text.dsCoreAdvertisement';
					}
				})
			);
		});

		// Set links for current language
		this.setLinks(this.translateService.currentLang);
		this.translateService.onLangChange.subscribe(async (language) => {
			await this.setCurrentLanguageAccordingToTranslationAvailability(language.lang);
			this.setLinks(language.lang);
		});
	}

	/**
	 * Set Cookie links for each language
	 * currently set to german, because only this one is available
	 *
	 * @param language
	 */
	public setLinks(language: string) {
		// todo change in other Links if available
		switch (language) {
			case 'resources':
				this.setEnglishLinks();
				break;
			case 'en-US':
				this.setEnglishLinks();
				break;
			case 'en-UK':
				this.setEnglishLinks();
				break;
			case 'de-DE':
				this.setGermanLinks();
				break;
			default:
				this.setEnglishLinks();
		}
	}

	/**
	 * German links
	 */
	public setGermanLinks() {
		this.aboutSironaLink = 'http://www.sirona-connect.de/ueber-sirona-connect/';
		this.serviceLink = 'http://www.sirona-connect.de/service/';
		this.contactLink = 'http://www.sirona-connect.de/kontakt/kontaktformular/';
	}

	/**
	 * English links
	 */
	public setEnglishLinks() {
		this.aboutSironaLink = 'http://www.sirona-connect.co.uk/about-sirona-connect/';
		this.serviceLink = 'http://www.sirona-connect.co.uk/service/';
		this.contactLink = 'http://www.sirona-connect.co.uk/contact/contact-form/';
	}

	/**
	 * On destroy
	 */
	ngOnDestroy() {
		// Destroy all subscriptions
		for (const subscription of this.subscriptions) {
			subscription.unsubscribe();
		}
	}

	/**
	 * Login
	 */
	login() {
		this.loader.loader.startLoading();
		this.subscriptions.push(
			this.configService.configObservable.subscribe((config) => {
				if (config) {
					this.windowRef.nativeWindow.location.href = config.loginUri + config.clientId;
				}
			})
		);

		return false;
	}

	openLink() {
		window.open(this.translateService.instant('header.button.checkDSCoreAvailabilityLink'), '_blank');
	}

	async setBannerForCountry(country: Country, extendedShutdownDate: Date, type: CompanyType) {
		if (country) {
			const now = (await this.getCurrentTime()) ?? new Date();
			if (country.endOfRegistration > now) {
				this.messageKey = 'header.text.dsCoreAdvertisement';
			} else if ((type === CompanyType.LAB && country.shutdownDateContractor) || country.shutdownDatePurchaser) {
				this.shutdownDate =
					type === CompanyType.LAB
						? extendedShutdownDate > country.shutdownDateContractor
							? extendedShutdownDate
							: country.shutdownDateContractor
						: extendedShutdownDate > country.shutdownDatePurchaser
						? extendedShutdownDate
						: country.shutdownDatePurchaser;
				this.messageKey = 'header.text.dsCoreAdvertisement_' + country.shutdownPhase + '_shutdown';
				await this.setCurrentLanguageAccordingToTranslationAvailability(this.translateService.currentLang);
			} else {
				this.messageKey = 'header.text.dsCoreAdvertisement';
			}
		} else {
			this.messageKey = 'header.text.dsCoreAdvertisement';
		}
	}

	async setCurrentLanguageAccordingToTranslationAvailability(languageCode: string) {
		let translations = await this.translateService.getTranslation(languageCode).toPromise();

		const splitKey = this.messageKey.split('.');
		let i = 0;
		while (i < splitKey.length) {
			translations = translations[splitKey[i]];
			i = i + 1;
		}

		if (!translations) {
			this.currentLanguage = 'en-US';
		} else {
			this.currentLanguage = languageCode === 'resources' ? 'en-US' : languageCode;
		}
	}

	async getCurrentTime(): Promise<Date | null> {
		// If the serverTime can be inferred from the stored values, calculate it.
		if (this.lastReceivedServerTime && this.lastServerTimeSynchronization) {
			return new Date(
				new Date().setTime(new Date(this.lastReceivedServerTime).getTime() + (performance.now() - this.lastServerTimeSynchronization))
			);
		}

		// Get the serverTime fresh from the server.
		this.lastServerTimeSynchronization = performance.now();
		const serverTime = await this.metaInfoService.GetServerTime().toPromise();
		this.lastReceivedServerTime = serverTime;
		return serverTime;
	}
}
